import { apiFetch } from "@/lib/api-client";
import { isFundLiveHere } from "@/lib/env-visibility";
import type { Fund as DdbFund } from "@etfsite/db/types";
import { useQuery } from "@tanstack/react-query";

/**
 * Public fund list — backed by `/api/data/funds` (DDB + Valkey cache, ~1 h TTL).
 *
 * Returns raw `DdbFund[]` with zero field-dropping. All consumers (FundCard,
 * HomePage, ProductsPage) read from the DDB shape directly:
 *   - `fund.id`        for the URL slug
 *   - `fund.fundType`  for category comparisons
 *   - `fundImageUrl(fund.imageKeys[0])` for the card image
 *
 * Feature flags (isLiveProduction, showPerformanceChart, showHoldingsTable,
 * featuredOnHomepage, etc.) all live on the DDB fund record — no separate
 * feature_flags API. Use `useFundConfig(ticker)` to read a single fund by ticker.
 */

interface ApiFundsResponse {
  funds: DdbFund[];
}

/**
 * Public fund list — filtered to funds live in THIS environment.
 *
 * Drafts (`isLive{Env} !== true`) are excluded everywhere: home page grid,
 * products listing, ticker tape, counts. They remain in DDB so admin can
 * promote them via the Copy-to-Env button, but never reach the public site
 * until that flag is flipped.
 */
export function useFundList() {
  return useQuery({
    queryKey: ["funds"],
    queryFn: () =>
      apiFetch<ApiFundsResponse>("/api/data/funds").then((r) => r.funds.filter(isFundLiveHere)),
    staleTime: 60 * 60 * 1000, // 1 hour — matches API cache TTL
  });
}

/**
 * Look up a single DDB fund by URL slug (`fund.id`).
 *
 * Reuses the shared `["funds"]` query cache — no extra API round-trip when
 * the list has already loaded (e.g. user came from the home or products page).
 * Falls back to a fresh list fetch when called directly (e.g. deep-link).
 */
export function useFundBySlug(slug: string | null | undefined) {
  return useQuery({
    queryKey: ["funds", "by-slug", slug?.toLowerCase()],
    queryFn: () =>
      apiFetch<ApiFundsResponse>("/api/data/funds").then((r) => {
        const fund = r.funds.find((f) => f.id === slug?.toLowerCase()) ?? null;
        // Hide non-live funds from public lookup — detail page treats null as 404.
        return fund && isFundLiveHere(fund) ? fund : null;
      }),
    enabled: Boolean(slug),
    staleTime: 60 * 60 * 1000,
  });
}

/**
 * Single raw DDB fund config by ticker.
 *
 * Returns the full `DdbFund` record. Used by `FundDetailPage` to read feature
 * flags directly from the fund record:
 *   - `fund.isLiveProduction` — env-visibility gate
 *   - `fund.showPerformanceChart` — performance chart section
 *   - `fund.showHoldingsTable` — holdings table section
 *   - `fund.showDailyNav` — P/D chart section
 *   - `fund.featuredOnHomepage` — featured grid on home page
 */
export function useFundConfig(ticker: string | null | undefined) {
  return useQuery({
    queryKey: ["fund", ticker?.toUpperCase()],
    queryFn: () =>
      apiFetch<{ fund: DdbFund }>(`/api/data/funds/${ticker?.toUpperCase()}`).then((r) => r.fund),
    enabled: Boolean(ticker),
    staleTime: 60 * 60 * 1000,
    // 404 = fund not in DDB yet — not an error, resolve to undefined.
    retry: (failureCount, error) => {
      if (error instanceof Error && error.message.includes("404")) return false;
      return failureCount < 1;
    },
  });
}
